import React, { useState } from 'react';

import { resolveActionPending } from '../../services/api/visitActionPendingApi';
import { visitPaymentDueRupees } from '../../utils/visitMoney';

/**
 * Phase 3.3 — CollectReferralPaymentDialog.
 *
 * Receptionist's "Collect" path on a parked REFERRAL_TO_DOCTOR overlay.
 * Records the payment but keeps the overlay parked — the receptionist
 * can forward later when the target doctor is free.
 *
 * Calls the existing resolve endpoint with `collect_only=true` (Phase
 * 3.3) so the service short-circuits before clearing overlay fields or
 * swapping the doctor pointer.
 *
 * Props:
 *   visit       — visit row carrying action_pending_payment_due (rupees,
 *                 Phase 3.5) + action_pending_payment_collected_paise
 *                 (running, paise).
 *   onSubmitted — (response) => void on successful collect.
 *   onCancel    — () => void
 */
export default function CollectReferralPaymentDialog({
  visit, onSubmitted, onCancel,
}) {
  // Visit row is rupees; the input + API contract here is paise. Convert
  // the rupees-due back to paise so the existing paise-based math works.
  const paymentDueRupees = visitPaymentDueRupees(visit);
  const paymentDuePaise = Math.round(paymentDueRupees * 100);
  const alreadyCollectedPaise = Number(
    visit?.action_pending_payment_collected_paise || 0,
  );
  const remainingPaise = Math.max(paymentDuePaise - alreadyCollectedPaise, 0);

  const [amountStr, setAmountStr] = useState(
    remainingPaise > 0 ? String(remainingPaise) : '',
  );
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const amountPaise = (() => {
    const trimmed = (amountStr || '').trim();
    if (!trimmed) return 0;
    const n = parseInt(trimmed, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();
  const canSubmit = amountPaise > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const resp = await resolveActionPending(visit.visit_id, {
        resolution: 'RESOLVED',  // ignored by collect_only path; required by schema
        comment: comment.trim() || null,
        payment_collected_paise: amountPaise,
        collect_only: true,
      });
      // eslint-disable-next-line no-console
      console.info(
        'metric=referral_collect_only value=1 visit=%s amount_paise=%d',
        visit.visit_id, amountPaise,
      );
      onSubmitted?.(resp);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to record payment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-1">
          Collect payment
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Patient: <strong>{visit?.patient_name || visit?.visit_id}</strong>
          {visit?.queue_number != null && (
            <span> · #{visit.queue_number}</span>
          )}
        </p>

        <div className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
          Payment will be recorded; the visit stays parked here until you
          forward it to the doctor.
        </div>

        {paymentDuePaise > 0 && (
          <div className="mb-2 text-xs text-slate-600 dark:text-slate-300">
            Quoted: ₹{(paymentDuePaise / 100).toFixed(2)}
            {alreadyCollectedPaise > 0 && (
              <> · already collected ₹{(alreadyCollectedPaise / 100).toFixed(2)}</>
            )}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            Amount (paise) *
          </label>
          <input
            type="number"
            min={1}
            step={1}
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            required
          />
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            ₹100 = 10000 paise.
          </p>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            Note (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            placeholder="e.g. Patient paid in cash"
          />
        </div>

        {errorMsg && (
          <div className="mb-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
          >
            {busy ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </form>
    </div>
  );
}
